import type { AxiosError } from 'axios';
import { ROUTE_PATHS } from '../routes/routePaths';
import { getAccessToken, getLoginUserId, getLoginUserOrgId, toBase64 } from '../util';
import { apiURL, codeVersion, errorLogsAPIBaseurl } from '../util/constants';
import { type DecodedToken, decodeJWT } from '../util/decodeJWT';
import { logger } from '../util/logger';
import { getBreadcrumbs, recordBreadcrumb, toSafeUrl } from './errorHandling/breadcrumbs.util';
import { classifyAxiosError } from './errorHandling/errorClassification.util';
import { computeErrorFingerprint, shouldThrottleErrorLog } from './errorHandling/errorFingerprint.util';
import type {
  ApiErrorLogDetails,
  ErrorClassification,
  ErrorLogPayload,
  ErrorLogSource,
  ErrorSeverity,
} from './errorHandling/errorHandling.types';
import { getSessionReplayContext } from './errorHandling/sessionReplay.util';
import { ErrorLogsClient } from './axiosConfig';

const SENSITIVE_HEADER_KEYS = ['authorization', 'cookie', 'x-api-key', 'token'];
// Matched against object keys (case-insensitively, substring) in request/response
// bodies before they're logged or persisted to the offline retry queue — headers
// alone aren't enough, since the sensitive data itself is usually in the body.
const SENSITIVE_BODY_KEY_PATTERNS = [
  'password',
  'passwd',
  'secret',
  'token',
  'apikey',
  'api_key',
  'authorization',
  'cookie',
  'ssn',
  'creditcard',
  'credit_card',
  'cardnumber',
  'cvv',
  'cvc',
  'pin',
];
const MAX_BODY_LENGTH = 5000;
const MAX_REDACTION_DEPTH = 6;
const MAX_CONSECUTIVE_LOG_FAILURES = 3;
const LAST_ERROR_PAYLOAD_STORAGE_KEY = 'errorLog:lastPayload';
const REUSABLE_PAYLOAD_STORAGE_PREFIX = 'errorLog:reusablePayload:';
// Per-correlationId copies of logged payloads, so an error page can look up the
// exact payload for the correlationId it was handed even if another error was
// logged in between (errorLog:lastPayload only ever holds the most recent one).
const PAYLOAD_BY_ID_STORAGE_PREFIX = 'errorLog:payloadById:';
const PAYLOAD_BY_ID_INDEX_STORAGE_KEY = 'errorLog:payloadByIdIndex';
const MAX_PAYLOADS_BY_ID = 20;
const LOGGED_CORRELATION_IDS_STORAGE_KEY = 'errorLog:loggedCorrelationIds';
const EMAILED_CORRELATION_IDS_STORAGE_KEY = 'errorLog:emailedCorrelationIds';
const PENDING_QUEUE_STORAGE_KEY = 'errorLog:pendingQueue';
// Unlike the pending queue, these never expire on their own — an error emailed a
// year ago is still an error that shouldn't be re-emailed. This cap is the only
// thing keeping localStorage from growing forever; oldest entries drop first.
const MAX_EMAILED_CORRELATION_IDS = 200;
// sessionStorage already clears itself per tab session, so this is just a sanity
// cap on how many "already delivered" correlationIds a single session accumulates.
const MAX_LOGGED_CORRELATION_IDS = 100;
// Bounds how much a prolonged outage can accumulate — an extreme case (e.g. a
// laptop closed overnight offline) shouldn't grow this without limit. Oldest
// entries are dropped first when the cap is hit.
const MAX_PENDING_QUEUE_SIZE = 20;
// Queued payloads can carry user context (name/email, request/response bodies) —
// bound how long that sits in plaintext localStorage if the browser never comes
// back online in this session.
const MAX_PENDING_ENTRY_AGE_MS = 48 * 60 * 60 * 1000;

let consecutiveLogFailures = 0;
let hasWarnedAboutMissingConfig = false;
// Guards against two overlapping `logError` calls for the same correlationId
// (e.g. a chunk-load failure whose retriable POST gets a stale 401 and is
// transparently retried by the axios response interceptor while this call is
// still awaiting the first attempt) sending the same payload twice. Without
// this, both calls pass `hasLoggedErrorSuccessfully` before either has a
// chance to mark itself delivered, and the same correlationId/message/route/
// timestamp ends up recorded twice — a real source of duplicate rows seen in
// production logs.
const inFlightCorrelationIds = new Set<string>();

function safeHeaderValueToString(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
}

function sanitizeHeaders(headers?: Record<string, unknown>): Record<string, string> | undefined {
  if (!headers) {
    return undefined;
  }
  const sanitized: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    const isSensitive = SENSITIVE_HEADER_KEYS.some((sensitiveKey) => key.toLowerCase().includes(sensitiveKey));
    sanitized[key] = isSensitive ? '[REDACTED]' : safeHeaderValueToString(value);
  }
  return sanitized;
}

function isSensitiveBodyKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_BODY_KEY_PATTERNS.some((pattern) => lowerKey.includes(pattern));
}

/** Recursively redacts values whose object key looks sensitive (password, token,
 * card number, ...) before a request/response body is ever logged or persisted —
 * headers are redacted separately (sanitizeHeaders), but the sensitive data itself
 * usually lives in the body, not the headers. Depth-capped defensively; JSON.stringify
 * would already throw on a circular structure before this matters. */
function redactSensitiveFields(value: unknown, depth = 0): unknown {
  if (depth >= MAX_REDACTION_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveFields(item, depth + 1));
  }
  const redacted: Record<string, unknown> = {};
  for (const [key, fieldValue] of Object.entries(value as Record<string, unknown>)) {
    redacted[key] = isSensitiveBodyKey(key) ? '[REDACTED]' : redactSensitiveFields(fieldValue, depth + 1);
  }
  return redacted;
}

/** Serialize FormData for logging as plain key-value. */
function serializeFormData(formData: FormData): string {
  const object: Record<string, string> = {};
  for (const [key, value] of formData.entries()) {
    object[key] = value instanceof File ? `[File: ${value.name}, ${value.size} bytes]` : String(value);
  }
  return JSON.stringify(object);
}

/** Blob/ArrayBuffer bodies (responseType 'blob' / 'arraybuffer' requests — PDF and
 * document downloads) JSON-stringify to a useless "{}". Describe them instead. */
function describeBinaryBody(value: unknown): string | undefined {
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return `[Blob: ${value.size} bytes${value.type ? `, ${value.type}` : ''}]`;
  }
  if (value instanceof ArrayBuffer) {
    return `[ArrayBuffer: ${value.byteLength} bytes]`;
  }
  return undefined;
}

function truncateBody(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  const binaryDescription = describeBinaryBody(value);
  if (binaryDescription) {
    return binaryDescription;
  }
  let serialized: string;
  try {
    if (typeof value === 'string') {
      serialized = value;
    } else if (typeof FormData !== 'undefined' && value instanceof FormData) {
      serialized = serializeFormData(value);
    } else {
      serialized = JSON.stringify(redactSensitiveFields(value));
    }
  } catch {
    return '[unable to serialize]';
  }
  return serialized.length > MAX_BODY_LENGTH ? `${serialized.slice(0, MAX_BODY_LENGTH)}…[truncated]` : serialized;
}

/** Best-effort — localStorage can throw or simply not persist (private mode, quota, disabled). */
function isExpiredPendingEntry(payload: ErrorLogPayload): boolean {
  const timestampMs = Date.parse(payload.timestamp);
  return !Number.isNaN(timestampMs) && Date.now() - timestampMs > MAX_PENDING_ENTRY_AGE_MS;
}

function writePendingQueue(queue: Array<ErrorLogPayload>): void {
  try {
    localStorage.setItem(PENDING_QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Not fatal — we just lose the retry-when-back-online behavior, not the app.
  }
}

function readPendingQueue(): Array<ErrorLogPayload> {
  try {
    const raw = localStorage.getItem(PENDING_QUEUE_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    const queue = Array.isArray(parsed) ? (parsed as Array<ErrorLogPayload>) : [];
    const fresh = queue.filter((payload) => !isExpiredPendingEntry(payload));
    if (fresh.length !== queue.length) {
      writePendingQueue(fresh);
    }
    return fresh;
  } catch {
    return [];
  }
}

async function postErrorLogPayload(payload: ErrorLogPayload): Promise<void> {
  const encodedPayload = toBase64(JSON.stringify(payload));
  await ErrorLogsClient.post(
    '/logs',
    { error: encodedPayload },
    {
      headers: {
        ...(payload.loggedInUserId ? { loginUserId: payload.loggedInUserId } : {}),
        ...(payload.loggedInUserOrgId ? { loginUserOrgId: payload.loggedInUserOrgId } : {}),
      },
    }
  );
}

function readLoggedCorrelationIds(): Array<string> {
  try {
    const raw = sessionStorage.getItem(LOGGED_CORRELATION_IDS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** Whether this correlationId's payload has already been delivered to the
 * error-logs API successfully this session — lets `logError` skip re-POSTing
 * the same error on a page refresh that reuses the same correlationId (see
 * getOrCreateErrorLogPayload) instead of logging it again on every reload. */
function hasLoggedErrorSuccessfully(correlationId: string): boolean {
  return readLoggedCorrelationIds().includes(correlationId);
}

/** Marks this correlationId as successfully delivered so a later refresh of the
 * same broken URL (same reused correlationId) doesn't re-POST it. */
function markErrorLoggedSuccessfully(correlationId: string): void {
  const ids = readLoggedCorrelationIds();
  if (ids.includes(correlationId)) {
    return;
  }
  try {
    sessionStorage.setItem(
      LOGGED_CORRELATION_IDS_STORAGE_KEY,
      JSON.stringify([...ids, correlationId].slice(-MAX_LOGGED_CORRELATION_IDS))
    );
  } catch {
    // Not fatal — worst case the same error gets re-POSTed on the next refresh.
  }
}

/** Single dispatch point for delivering a built error-log payload. Automatic
 * logging must never send an email — that's exclusively the user-initiated
 * `emailErrorLog` action below — so this always targets `/logs`. If the pre-T&C
 * window means there's no auth cookie yet, the post below fails like any other
 * outage and `logError`'s existing queue/retry handles it. */
function deliverErrorLogPayload(payload: ErrorLogPayload): Promise<void> {
  return postErrorLogPayload(payload);
}

/**
 * Retries every payload that couldn't be delivered earlier (queued by `logError`
 * below). Called once at startup (in case entries survived a reload from a
 * previous offline session), on the browser's `online` event, and opportunistically
 * right after any single `logError` failure if the browser already reports itself
 * online by then.
 */
export async function flushPendingErrorLogs(): Promise<void> {
  const queue = readPendingQueue();
  if (queue.length === 0) {
    return;
  }

  const deliveredCorrelationIds = new Set<string>();
  for (const payload of queue) {
    // Already delivered (its POST succeeded but the page unloaded before it could be
    // dequeued) — just drop it. Still in flight in this tab — let that POST finish.
    if (hasLoggedErrorSuccessfully(payload.correlationId)) {
      deliveredCorrelationIds.add(payload.correlationId);
      continue;
    }
    if (inFlightCorrelationIds.has(payload.correlationId)) {
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      await deliverErrorLogPayload(payload);
      consecutiveLogFailures = 0;
      deliveredCorrelationIds.add(payload.correlationId);
      markErrorLoggedSuccessfully(payload.correlationId);
    } catch {
      // Stays queued — picked up on the next flush attempt.
    }
  }

  if (deliveredCorrelationIds.size === 0) {
    return;
  }

  const latestQueue = readPendingQueue();
  const remaining = latestQueue.filter((payload) => !deliveredCorrelationIds.has(payload.correlationId));
  writePendingQueue(remaining);

  logger.log(`Flushed ${deliveredCorrelationIds.size} pending error log(s); ${remaining.length} still pending`);
}

/** A payload that couldn't be delivered right now — persisted so it survives a
 * redirect/reload and gets retried once the browser is back online. */
/** Idempotent per correlationId — the payload is queued up front by logError (see
 * there) and may be queued again on failure; it must only ever be sent once. */
function enqueuePendingErrorLog(payload: ErrorLogPayload): void {
  const queue = readPendingQueue().filter((queued) => queued.correlationId !== payload.correlationId);
  writePendingQueue([...queue, payload].slice(-MAX_PENDING_QUEUE_SIZE));
}

function removePendingErrorLog(correlationId: string): void {
  const queue = readPendingQueue();
  const remaining = queue.filter((queued) => queued.correlationId !== correlationId);
  if (remaining.length !== queue.length) {
    writePendingQueue(remaining);
  }
}

/** Enqueues, then opportunistically retries right away if the browser already
 * reports itself online — closes the gap between the one-shot `online` event
 * and a `logError` POST that was still in flight when it fired. */
function enqueueAndMaybeRetryNow(payload: ErrorLogPayload): void {
  enqueuePendingErrorLog(payload);
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void flushPendingErrorLogs();
  }
}

function rememberErrorLogPayloadById(payload: ErrorLogPayload): void {
  try {
    const raw = sessionStorage.getItem(PAYLOAD_BY_ID_INDEX_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const index = Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string' && id !== payload.correlationId)
      : [];
    index.push(payload.correlationId);
    while (index.length > MAX_PAYLOADS_BY_ID) {
      const evicted = index.shift();
      if (evicted) {
        sessionStorage.removeItem(PAYLOAD_BY_ID_STORAGE_PREFIX + evicted);
      }
    }
    sessionStorage.setItem(PAYLOAD_BY_ID_STORAGE_PREFIX + payload.correlationId, JSON.stringify(payload));
    sessionStorage.setItem(PAYLOAD_BY_ID_INDEX_STORAGE_KEY, JSON.stringify(index));
  } catch {
    // Not fatal — getRememberedErrorLogPayload still falls back to the last payload.
  }
}

/** Best-effort — powers the "email this error" support action, not core
 * logging, so a storage failure here must never affect logError. */
function rememberErrorLogPayload(payload: ErrorLogPayload): void {
  try {
    sessionStorage.setItem(LAST_ERROR_PAYLOAD_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Not fatal — the error page just falls back to a smaller reconstructed JSON.
  }
  rememberErrorLogPayloadById(payload);
}

/** Retrieves the exact payload logged for this correlationId this session — the
 * per-id copy first, then the most recent payload as a fallback. */
export function getRememberedErrorLogPayload(correlationId: string): ErrorLogPayload | null {
  try {
    const byId = sessionStorage.getItem(PAYLOAD_BY_ID_STORAGE_PREFIX + correlationId);
    if (byId) {
      return JSON.parse(byId) as ErrorLogPayload;
    }
    const raw = sessionStorage.getItem(LAST_ERROR_PAYLOAD_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as ErrorLogPayload;
    return parsed.correlationId === correlationId ? parsed : null;
  } catch {
    return null;
  }
}

const ATTACHED_ERROR_LOG_PAYLOAD_KEY = 'aosErrorLogPayload';

interface ErrorWithLogPayload {
  [ATTACHED_ERROR_LOG_PAYLOAD_KEY]?: ErrorLogPayload;
}

/**
 * Attaches the payload already logged for a failure (e.g. by the axios
 * interceptor, whose correlationId is the request's X-Request-ID) onto the error
 * object itself. Anything downstream that handles the same failure — a page
 * redirecting to /standard-error, an ErrorBoundary, the global listeners — reads
 * it back with `getAttachedErrorLogPayload` / `getErrorLogCorrelationId` and
 * reuses that correlationId instead of minting (and logging) a second one for
 * what is the same root cause.
 */
export function attachErrorLogPayload(error: unknown, payload: ErrorLogPayload): void {
  if (error && typeof error === 'object') {
    try {
      Object.defineProperty(error, ATTACHED_ERROR_LOG_PAYLOAD_KEY, {
        value: payload,
        configurable: true,
        writable: true,
        enumerable: false,
      });
    } catch {
      // Frozen/sealed object — nothing to attach to; downstream just mints its own.
    }
  }
}

/** Reads back the payload attached by `attachErrorLogPayload`, if any. */
export function getAttachedErrorLogPayload(error: unknown): ErrorLogPayload | undefined {
  return error && typeof error === 'object'
    ? (error as ErrorWithLogPayload)[ATTACHED_ERROR_LOG_PAYLOAD_KEY]
    : undefined;
}

/** The correlationId already logged for this error (see `attachErrorLogPayload`),
 * if any — pass it along (e.g. in /standard-error route state) so the error page
 * shows and emails the same Ref. that is in /logs. */
export function getErrorLogCorrelationId(error: unknown): string | undefined {
  return getAttachedErrorLogPayload(error)?.correlationId;
}

/** Returns the payload already built for this key this session, if any, otherwise
 * builds one via `build()` and remembers it — keeps the correlationId/timestamp
 * shown on an error page (NotFound, StandardError's document-error branch) stable
 * across a hard refresh of the same broken URL instead of minting a fresh one on
 * every mount. A genuinely different `key` (e.g. a different broken URL) still
 * gets its own fresh payload. */
export function getOrCreateErrorLogPayload(
  key: string,
  build: () => ErrorLogPayload
): ErrorLogPayload {
  const storageKey = REUSABLE_PAYLOAD_STORAGE_PREFIX + key;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      return JSON.parse(raw) as ErrorLogPayload;
    }
  } catch {
    // Fall through and build a fresh payload below.
  }

  const payload = build();
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Not fatal — worst case the Ref./timestamp regenerate on the next refresh.
  }
  return payload;
}

function readEmailedCorrelationIds(): Array<string> {
  try {
    const raw = localStorage.getItem(EMAILED_CORRELATION_IDS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** Whether this correlationId's error has already been emailed — used to permanently
 * disable the "email this error" button, including on a fresh visit to the same link. */
export function hasEmailedErrorLog(correlationId: string): boolean {
  return readEmailedCorrelationIds().includes(correlationId);
}

/** Marks this correlationId as emailed so the button stays disabled on any future visit. */
export function markErrorLogEmailed(correlationId: string): void {
  const ids = readEmailedCorrelationIds();
  if (ids.includes(correlationId)) {
    return;
  }
  try {
    localStorage.setItem(
      EMAILED_CORRELATION_IDS_STORAGE_KEY,
      JSON.stringify([...ids, correlationId].slice(-MAX_EMAILED_CORRELATION_IDS))
    );
  } catch {
    // Not fatal — worst case the button becomes clickable again after a reload.
  }
}

export interface ErrorEmailPayload {
  subject: string;
  body: string;
  /** Enterprise reCAPTCHA result — omitted entirely for an authenticated sender,
   * since the authenticated `/errors/email` API doesn't require captcha verification.
   * Mandatory the moment `emailErrorLog` needs `/public/errors/email` instead (see
   * `CaptchaRequiredError`) — whether that's known upfront (a fully-public route) or
   * only discovered mid-call (the authenticated attempt came back 401). */
  captcha?: { token: string; action: string };
}

/**
 * Thrown by `emailErrorLog` in place of ever calling `/public/errors/email` without a
 * captcha — that endpoint requires CAPTCHA verification unconditionally, and this is
 * the single choke point both call sites (the fully-public-route path and the
 * authenticated-401 fallback) go through, so no caller can accidentally send that
 * request without a captcha. A caller that hits this needs to obtain a captcha token
 * (e.g. render the reCAPTCHA widget) and retry with `payload.captcha` set — see
 * EmailErrorDialog's handling of this error.
 */
export class CaptchaRequiredError extends Error {
  public constructor() {
    super('CAPTCHA verification is required to send this without an active session.');
    this.name = 'CaptchaRequiredError';
  }
}

/** Duck-typed instead of `axios.isAxiosError` — that's a static method on the real
 * axios module, which test doubles for `axios` don't provide, and the shape check
 * is all `emailErrorLog` actually needs. */
function isHttpErrorWithStatus(error: unknown, status: number): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === status
  );
}

/** Every route reachable before full login completes — T&C, OTP, SSO OTP,
 * silent SSO callback, SSO error. Mirrors aosphere-nextgenui-user's
 * `preLoginPageRoutes` (utils/routes.ts). */
const PRE_LOGIN_SAFE_ROUTES: ReadonlySet<string> = new Set([
  ROUTE_PATHS.SILENT_SSO_CALLBACK,
  ROUTE_PATHS.TERMS_AND_CONDITIONS,
  ROUTE_PATHS.OTP_VERIFICATION,
  ROUTE_PATHS.SSO_OTP_VERIFICATION,
  ROUTE_PATHS.SSO_ERROR,
]);

function isOnPreLoginRoute(): boolean {
  return typeof window !== 'undefined' && PRE_LOGIN_SAFE_ROUTES.has(window.location.pathname);
}

/**
 * `ErrorLogsClient` (used by both `emailErrorLog` and `postErrorLogPayload`) relies
 * solely on the `isLoggedIn` cookie via `withCredentials` — but that cookie isn't set
 * until full login (T&C acceptance) completes, so on any pre-login-safe route there's
 * no valid credential at all yet. Attaches the same explicit
 * `Authorization: Bearer <access_token>` header authService.ts's pre-T&C-safe
 * calls already use for these routes. Returns `{}` on
 * any fully-authenticated page, leaving the existing cookie-only behavior untouched.
 */
function buildPreLoginAuthHeaders(): Record<string, string | undefined> {
  if (!isOnPreLoginRoute()) {
    return {};
  }
  const accessToken = getAccessToken();
  const loginUserId = getLoginUserId();
  const loginUserOrgId = getLoginUserOrgId();
  return {
    Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
    ...(loginUserId ? { loginUserId } : {}),
    ...(loginUserOrgId ? { loginUserOrgId } : {}),
  };
}

/** Routes reachable with no login attempt at all — no access_token/loginUserId
 * exists in localStorage the way it does mid-login (T&C/OTP), so there's nothing
 * for `/errors/email` to authenticate with. Goes straight to the public endpoint
 * instead of trying (and failing) the authenticated one first. */
const FULLY_PUBLIC_ROUTES: ReadonlySet<string> = new Set([
  ROUTE_PATHS.FORGOT_PASSWORD,
  ROUTE_PATHS.RESET_PASSWORD,
]);

function isOnFullyPublicRoute(): boolean {
  return typeof window !== 'undefined' && FULLY_PUBLIC_ROUTES.has(window.location.pathname);
}

/** True for any route reachable before full login completes — PRE_LOGIN_SAFE_ROUTES
 * plus FULLY_PUBLIC_ROUTES. Used to flag the "email this error" subject with a
 * `(pre-login)` tag, given an error log payload's own `route` rather than the
 * current `window.location` (the payload may be a remembered one from a route the
 * user already navigated away from). */
export function isPreLoginPageRoute(route: string): boolean {
  return PRE_LOGIN_SAFE_ROUTES.has(route) || FULLY_PUBLIC_ROUTES.has(route);
}

/**
 * Sends the "email this error" subject/body to the error-logs service. Unlike
 * `logError`, this DOES throw on failure: it's a direct user action (not background
 * telemetry), so the caller needs to know it failed in order to leave the button
 * enabled for a retry instead of marking the error emailed. Throws
 * `CaptchaRequiredError` — instead of ever posting to `/public/errors/email` without
 * one — whenever a captcha would be needed but `payload.captcha` wasn't supplied.
 *
 * A 401 here means even the pre-login Bearer/cookie attempt wasn't accepted (e.g.
 * an expired access token, or a page outside PRE_LOGIN_SAFE_ROUTES) — falls back
 * once to the public, unauthenticated `/public/errors/email` endpoint with the
 * same subject/body/captcha and no auth headers at all. On FULLY_PUBLIC_ROUTES
 * (forgot/reset password — no login attempt has happened at all there), skips
 * straight to the public endpoint instead of trying (and failing) the
 * authenticated one first.
 */
export async function emailErrorLog(payload: ErrorEmailPayload): Promise<void> {
  const body: { subject: string; body: string; action?: string; token?: string } = {
    subject: payload.subject,
    body: toBase64(payload.body),
  };
  if (payload.captcha) {
    body.action = payload.captcha.action;
    body.token = payload.captcha.token;
  }

  if (isOnFullyPublicRoute()) {
    if (!payload.captcha) {
      throw new CaptchaRequiredError();
    }
    await ErrorLogsClient.post('/public/errors/email', body);
    return;
  }

  try {
    await ErrorLogsClient.post('/errors/email', body, { headers: buildPreLoginAuthHeaders() });
  } catch (error) {
    if (isHttpErrorWithStatus(error, 401)) {
      if (!payload.captcha) {
        throw new CaptchaRequiredError();
      }
      await ErrorLogsClient.post('/public/errors/email', body);
      return;
    }
    throw error;
  }
}

type StoredUserData = Record<string, unknown>;

function readStorageJson(storage: Storage | undefined, key: string): StoredUserData | null {
  try {
    const raw = storage?.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as StoredUserData) : null;
  } catch {
    return null;
  }
}

function readStorageString(storage: Storage | undefined, key: string): string {
  try {
    return storage?.getItem(key)?.trim() ?? '';
  } catch {
    return '';
  }
}

/** First non-empty string (numbers stringified) among the candidates. */
function firstNonEmpty(...values: Array<unknown>): string {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    if (typeof value === 'string' && value.trim() && value.trim() !== 'null' && value.trim() !== 'undefined') {
      return value.trim();
    }
  }
  return '';
}

function joinName(first: unknown, last: unknown): string {
  const parts = [first, last]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .map((part) => part.trim());
  return parts.join(' ');
}

function decodeStoredToken(key: string): DecodedToken | null {
  const token = readStorageString(globalThis.localStorage, key);
  return token ? decodeJWT(token) : null;
}

/** Reads the currently signed-in user synchronously — `authService.getUserInfo()`
 * is `Promise`-typed but reads localStorage under the hood, so this mirrors it
 * without forcing every caller (componentDidCatch, window.onerror) to be async.
 *
 * Always returns all four fields as strings — never `undefined` — so every error
 * log payload has the same shape to filter/group by; an empty string means "not
 * known at this point", not "field doesn't exist" (a payload built with `undefined`
 * here would have the key dropped entirely by `JSON.stringify` in `postErrorLogPayload`,
 * which is why these previously showed up on some log entries and not others even
 * though the field was always intended to be there).
 *
 * `userData` (the name/email source) is now read directly from localStorage the same
 * unconditional way `loginUserId`/`loginUserOrgId` already are, instead of being
 * gated behind `authService.isAuthenticated()` (the cookie set only once T&C
 * acceptance completes). Login/index.tsx writes `userData` to localStorage at the
 * exact same step it writes `loginUserId`/`loginUserOrgId` — well before that cookie
 * exists (see `handleRequestOTP` / the SSO terms-condition flow) — so the extra gate
 * only ever caused loggedInUserName/Email to go missing on errors raised in that
 * in-between window (OTP, T&C, a failed OIDC callback, ...) while the id/org id from
 * that very same write kept showing up right next to them on the same log entry.
 */
function getLoggedInUserContext(): {
  loggedInUserId: string;
  loggedInUserOrgId: string;
  loggedInUserName: string;
  loggedInUserEmail: string;
} {
  // Each field falls back through every place this app keeps the signed-in user, so
  // an error raised before `userData` is written (OIDC callback, OTP, T&C, a legacy
  // link landing, a different userData shape from the SSO/OTP/cookie endpoints, ...)
  // still carries whatever identity is already known: `userData` first (the richest
  // source), then the Keycloak access/id token claims, then login-flow leftovers.
  const userData = readStorageJson(globalThis.localStorage, 'userData');
  const accessClaims = decodeStoredToken('access_token');
  const idClaims = decodeStoredToken('id_token');

  const loginUserId = firstNonEmpty(getLoginUserId(), userData?.userID, userData?.userId);
  const loginUserOrgId = firstNonEmpty(
    getLoginUserOrgId(),
    userData?.orgId,
    userData?.orgID,
    accessClaims?.['Custome-claim']?.orgId,
    idClaims?.['Custome-claim']?.orgId
  );
  const name = firstNonEmpty(
    joinName(userData?.firstName, userData?.lastName),
    userData?.fullName,
    userData?.name,
    userData?.userName,
    accessClaims?.name,
    joinName(accessClaims?.given_name, accessClaims?.family_name),
    idClaims?.name,
    joinName(idClaims?.given_name, idClaims?.family_name)
  );
  const email = firstNonEmpty(
    userData?.emailAddress,
    userData?.userEmail,
    userData?.email,
    accessClaims?.email,
    idClaims?.email,
    readStorageString(globalThis.sessionStorage, 'oidcLoginEmail')
  );

  return {
    loggedInUserId: loginUserId,
    loggedInUserOrgId: loginUserOrgId,
    loggedInUserName: name,
    loggedInUserEmail: email,
  };
}

/**
 * Dev/prod detection is deliberately based on the API base URL (same convention as
 * util/logger.ts), not NODE_ENV — every *deployed* CRA build reports NODE_ENV as
 * "production", including dev1/qa/stage, so NODE_ENV can't distinguish them.
 */
function getEnvironmentLabel(): string {
  if (apiURL?.toUpperCase().includes('DEV')) {
    return 'development';
  }
  return process.env.NODE_ENV ?? 'production';
}

/**
 * Builds the common envelope shared by every error log, regardless of source —
 * correlationId, route/pageUrl/timestamp/userAgent, environment, app version, and
 * the logged-in user's identity. Fully synchronous so it can be called from
 * componentDidCatch / window.onerror without an async boundary.
 */
export function buildBaseErrorLogPayload(
  source: ErrorLogSource,
  severity: ErrorSeverity,
  message: string,
  stack?: string
): Omit<ErrorLogPayload, 'componentName' | 'componentStack' | 'api'> {
  const breadcrumbs = getBreadcrumbs();
  const sessionReplay = getSessionReplayContext();
  return {
    correlationId: crypto.randomUUID(),
    mainSource: 'V2 user',
    source,
    severity,
    message,
    stack,
    route: typeof window === 'undefined' ? 'unknown' : window.location.pathname,
    pageUrl: typeof window === 'undefined' ? 'unknown' : window.location.href,
    timestamp: new Date().toISOString(),
    userAgent: typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent,
    environment: getEnvironmentLabel(),
    appVersion: process.env.REACT_APP_VERSION ?? codeVersion,
    ...getLoggedInUserContext(),
    ...(breadcrumbs.length > 0 ? { breadcrumbs } : {}),
    ...sessionReplay,
  };
}

function extractApiParameters(parameters: unknown): Record<string, unknown> | undefined {
  return parameters != null && typeof parameters === 'object' && !Array.isArray(parameters) ? (parameters as Record<string, unknown>) : undefined;
}

function stringifyQueryParameterValue(value: unknown): string {
  switch (typeof value) {
    case 'string': {
      return value;
    }
    case 'number':
    case 'boolean':
    case 'bigint': {
      return String(value);
    }
    default: {
      return JSON.stringify(value);
    }
  }
}

/** Resolves the full request URL, appending `params` as a query string if it wasn't already embedded in `url`. */
function buildFullApiUrl(url: string, baseURL: string, parameters: Record<string, unknown> | undefined): string {
  const resolvedUrl = url.startsWith('http') ? url : `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  if (!parameters) {
    return resolvedUrl;
  }
  const searchParameters = new URLSearchParams();
  for (const [key, value] of Object.entries(parameters)) {
    if (value !== undefined && value !== null) {
      searchParameters.append(
        key,
        stringifyQueryParameterValue(value)
      );
    }
  }
  const queryString = searchParameters.toString();
  if (!queryString) {
    return resolvedUrl;
  }
  const separator = resolvedUrl.includes('?') ? '&' : '?';
  return `${resolvedUrl}${separator}${queryString}`;
}

/** Builds the `api` sub-object attached to API-sourced error payloads — the axios
 * interceptor calls this once, on the final unsuccessful attempt only. */
// Error bodies larger than this are almost certainly not an API error message (e.g.
// a real file that came back with an error status) — don't read them into memory.
const MAX_BINARY_ERROR_BODY_BYTES = 64 * 1024;
const CAPTURED_RESPONSE_BODY_KEY = 'aosCapturedResponseBody';

type ResponseWithCapturedBody = { [CAPTURED_RESPONSE_BODY_KEY]?: unknown };

function looksLikeTextContentType(contentType: string): boolean {
  return contentType === '' || /json|text|xml|problem/i.test(contentType);
}

/**
 * For requests made with `responseType: 'blob'` / `'arraybuffer'` (PDF viewer ranges,
 * view-doc/storage downloads, ...), axios hands back the server's error body — e.g.
 * `{"errors":[{"message":"…"}]}` — as a Blob/ArrayBuffer, which the log used to show
 * as `"body": "{}"`. Reads it as text (parsed as JSON when possible, so sensitive
 * fields are still redacted) and keeps it on the response for
 * `buildApiErrorLogDetails`. `error.response.data` itself is left untouched, so
 * callers that already parse the Blob themselves keep working. Async because
 * Blob.text() is — call it from the (already async) response-error interceptor
 * before anything logs. Never throws.
 */
export async function captureBinaryErrorResponseBody(error: unknown): Promise<void> {
  const response = (error as AxiosError | undefined)?.response;
  if (!response || typeof response !== 'object') {
    return;
  }
  const data: unknown = response.data;
  const isBlob = typeof Blob !== 'undefined' && data instanceof Blob;
  const isArrayBuffer = data instanceof ArrayBuffer;
  if (!isBlob && !isArrayBuffer) {
    return;
  }
  const size = isBlob ? (data).size : (data as ArrayBuffer).byteLength;
  const contentTypeHeader: unknown = (response.headers as Record<string, unknown> | undefined)?.['content-type'];
  const headerContentType = typeof contentTypeHeader === 'string' ? contentTypeHeader : '';
  const contentType = headerContentType || (isBlob ? (data).type : '');
  if (size === 0 || size > MAX_BINARY_ERROR_BODY_BYTES || !looksLikeTextContentType(contentType)) {
    return;
  }
  try {
    const text = isBlob ? await (data).text() : new TextDecoder().decode(data as ArrayBuffer);
    let captured: unknown = text;
    try {
      captured = JSON.parse(text);
    } catch {
      // Not JSON — keep the raw text.
    }
    Object.defineProperty(response, CAPTURED_RESPONSE_BODY_KEY, {
      value: captured,
      configurable: true,
      writable: true,
      enumerable: false,
    });
  } catch {
    // Unreadable body — the log falls back to describing the Blob.
  }
}

function getResponseBodyForLog(response: AxiosError['response']): unknown {
  if (!response) {
    return undefined;
  }
  const captured = (response as unknown as ResponseWithCapturedBody)[CAPTURED_RESPONSE_BODY_KEY];
  return captured === undefined ? response.data : captured;
}

export function buildApiErrorLogDetails(
  error: AxiosError,
  classification: ErrorClassification,
  retryAttempt: number,
  maxRetries: number,
  isFinalAttempt: boolean
): ApiErrorLogDetails {
  const config = error.config;
  const url = config?.url ?? 'unknown';
  const baseURL = config?.baseURL ?? '';
  const apiParameters = extractApiParameters(config?.params);

  return {
    apiUrl: buildFullApiUrl(url, baseURL, apiParameters),
    apiMethod: (config?.method ?? 'GET').toUpperCase(),
    apiParams: apiParameters,
    apiHeaders: sanitizeHeaders(config?.headers),
    apiBody: truncateBody(config?.data),
    apiResponse: {
      status: classification.statusCode,
      headers: sanitizeHeaders(error.response?.headers),
      body: truncateBody(getResponseBodyForLog(error.response)),
    },
    errorCode: error.code,
    isOnline: typeof navigator === 'undefined' ? undefined : navigator.onLine,
    category: classification.category,
    retryAttempt,
    maxRetries,
    isFinalAttempt,
  };
}

/**
 * Sends a fully-built error payload to the shared error-logs service. Never throws —
 * a broken logging endpoint must never crash the app it's trying to instrument.
 * Failures are counted and, past a threshold, further attempts are skipped until one
 * succeeds (circuit breaker) — but every payload that can't be delivered right now
 * (network down, circuit open) is persisted to localStorage first, so
 * `flushPendingErrorLogs` can retry it once the browser reports it's back online,
 * even across a redirect/reload.
 */
export async function logError(payload: ErrorLogPayload): Promise<void> {
  // A failed API call becomes a breadcrumb for whatever error comes next (this
  // payload already captured the trail as it was before this failure).
  if (payload.api) {
    recordBreadcrumb({
      kind: 'request-failed',
      detail: `${payload.api.apiMethod} ${toSafeUrl(payload.api.apiUrl)}`,
      status: payload.api.apiResponse?.status ?? 0,
    });
  }

  // Remembered unconditionally — the error page's "email this error" action needs
  // the exact payload for whatever correlationId it's about to show, regardless of
  // whether the POST below succeeds, is throttled, or the logs endpoint isn't
  // even configured.
  rememberErrorLogPayload(payload);

  // A page refresh that reuses the same correlationId (see getOrCreateErrorLogPayload)
  // would otherwise re-POST an error that's already been delivered successfully —
  // skip it once we know this exact correlationId already got a 200.
  if (hasLoggedErrorSuccessfully(payload.correlationId)) {
    return;
  }

  // A second call for a correlationId whose delivery is already in flight is either
  // a duplicate componentDidCatch/listener firing for the same error, or a caller
  // re-entering before the first attempt has settled — either way, let the one
  // already in flight finish instead of sending the same payload again.
  if (inFlightCorrelationIds.has(payload.correlationId)) {
    return;
  }

  logger.error('🚨 Error log payload', payload);

  if (!errorLogsAPIBaseurl) {
    if (!hasWarnedAboutMissingConfig) {
      hasWarnedAboutMissingConfig = true;
      // eslint-disable-next-line no-console -- a missing config is exactly the kind
      // of failure that must stay visible in every environment.
      console.warn('Error log skipped: REACT_APP_API_ERROR_LOGS_URL is not configured', payload);
    }
    return;
  }

  if (consecutiveLogFailures >= MAX_CONSECUTIVE_LOG_FAILURES) {
    enqueueAndMaybeRetryNow(payload);
    // eslint-disable-next-line no-console
    console.warn('Error log queued: logging endpoint has failed repeatedly — will retry once back online', payload);
    return;
  }

  const fingerprint = computeErrorFingerprint([
    payload.source,
    payload.message,
    payload.route,
    // Two different components failing with the same generic message on the same
    // route within the throttle window are distinct bugs, not repeats of one.
    payload.componentName,
    payload.childComponentName,
    payload.api?.apiResponse?.status,
    payload.api?.retryAttempt,
  ]);

  if (shouldThrottleErrorLog(fingerprint)) {
    return;
  }

  // Write-ahead: persist the payload BEFORE sending it. Many error flows navigate
  // away with a full page load right after logging (window.location.assign /
  // replace — e.g. the 400 from sharedRedirectFromCfmHref → /standard-error, the eURL
  // decrypt → /not-found redirect), which aborts this POST before it's sent, so the
  // error never reached /logs. Queued here, it's removed once delivered and
  // otherwise sent by flushPendingErrorLogs on the next page load (it runs at
  // startup in useGlobalErrorListeners) — with the same correlationId.
  enqueuePendingErrorLog(payload);
  inFlightCorrelationIds.add(payload.correlationId);
  try {
    await deliverErrorLogPayload(payload);
    // eslint-disable-next-line require-atomic-updates -- fine: isLoggingError-style guard isn't needed here since this is the only writer within this await's lifetime
    consecutiveLogFailures = 0;
    markErrorLoggedSuccessfully(payload.correlationId);
    removePendingErrorLog(payload.correlationId);
  } catch (postError) {
    consecutiveLogFailures += 1;
    enqueueAndMaybeRetryNow(payload);
    // eslint-disable-next-line no-console -- a failed log POST must stay visible in
    // every environment, or it looks like nothing happened with no way to tell why.
    console.warn('Error log POST failed — queued for retry once back online', postError);
  } finally {
    inFlightCorrelationIds.delete(payload.correlationId);
  }
}

export function resetErrorLogCircuitBreaker(): void {
  consecutiveLogFailures = 0;
}

/** Duck-typed the same way `isHttpErrorWithStatus` above is — catches axios-error-shaped
 * values regardless of whether they're a real `AxiosError` instance, including a value
 * that only got there via an `error as AxiosError` type assertion (which asserts nothing
 * at runtime). Lets `logGlobalError` attach full request/response detail even for call
 * sites that bypass the shared axios interceptor in axiosConfig.ts entirely (e.g.
 * ssoAuthService.ts's raw `axios.post` calls, which have never had `apiUrl`/`apiResponse`
 * on their logs). */
function looksLikeAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('isAxiosError' in error || 'response' in error || 'config' in error)
  );
}

/**
 * Backward-compatible convenience wrapper for call sites that only have a raw
 * Error/AxiosError and don't need the full structured payload (component name,
 * api details, origin frame, etc.) — builds a base payload and logs it. Prefer
 * `buildBaseErrorLogPayload` + `logError` directly for anything that can supply
 * richer context (see ErrorBoundary.tsx / axiosConfig.ts for examples).
 *
 * Takes `unknown` (not just `Error | string | AxiosError`) because a caught value is
 * never actually guaranteed to be any of those at runtime — a plain rejected object,
 * for instance — and production logs have shown exactly that: a caller passing
 * something that was neither an `Error` nor a `string` used to fall straight through
 * into `message`, producing a content-free `message: {}` log entry with no stack and
 * nothing to trace. That's handled below instead of silently corrupting the payload.
 */
export async function logGlobalError(
  error: unknown,
  stack?: string,
  source: ErrorLogSource = 'ui-action'
): Promise<void> {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : `Non-Error value logged: ${truncateBody(error) ?? String(error)}`;
  const errorStack = stack ?? (error instanceof Error ? error.stack : undefined);
  const basePayload = buildBaseErrorLogPayload(source, 'error', message, errorStack);
  const payload: ErrorLogPayload = looksLikeAxiosError(error)
    ? { ...basePayload, api: buildApiErrorLogDetails(error, classifyAxiosError(error), 0, 0, true) }
    : basePayload;
  await logError(payload);
}

export function ensureUpstreamErrorLogDelivered(correlationId: string): void {
  // An error page handed an upstream correlationId must not log a NEW entry, but it
  // must make sure the upstream one actually reached /logs: its POST may have been
  // aborted by a full-page redirect (window.location.assign/replace) before it was
  // sent. The payload was remembered per-id in sessionStorage when logError ran, so
  // re-send it — logError itself skips it if it was already delivered or is still
  // in flight, so this never creates a duplicate within the session.
  if (hasLoggedErrorSuccessfully(correlationId) || inFlightCorrelationIds.has(correlationId)) {
    return;
  }
  const payload = getRememberedErrorLogPayload(correlationId);
  if (payload) {
    void logError(payload);
  }
}

/**
 * For error pages handed an upstream correlationId: returns the exact payload that
 * was already logged for it, or — if it's no longer in sessionStorage (e.g. the
 * page was opened in a new tab) — a minimal payload that still carries that same
 * correlationId. Either way the caller must NOT log it again.
 */
export function resolveUpstreamErrorLogPayload(correlationId: string, fallbackMessage: string): ErrorLogPayload {
  return (
    getRememberedErrorLogPayload(correlationId) ?? {
      ...buildBaseErrorLogPayload('ui-action', 'error', fallbackMessage),
      correlationId,
    }
  );
}
